"use client";

import { useRef } from "react";
import { gsap, useGSAP } from "@/lib/gsap";
import { blurReveal, fadeUp, reduceMotion } from "@/lib/motion";
import { TextReveal } from "@/components/motion/TextReveal";
import { Eyebrow } from "@/components/ui/Eyebrow";
import { ArrowUpRight } from "@/components/ui/icons";
import { DarkPanel } from "@/components/sections/DarkPanel";
import { BLOG, BLOG_FEATURED, postUrl } from "@/content/blog";
import { PostCover } from "./PostCover";

const GRADIENT = "linear-gradient(90deg, #ffffff 0%, #f5f5f5 40%, #9a9aa0 100%)";

/**
 * Blog masthead: label, headline de-blurs word by word, the standfirst, then the featured
 * story rises into the stage.
 */
export function BlogHero() {
  const root = useRef<HTMLDivElement>(null);
  const post = BLOG_FEATURED;

  useGSAP(
    () => {
      const el = root.current;
      if (!el || reduceMotion()) return;
      const q = (s: string) => gsap.utils.toArray<HTMLElement>(s, el);
      gsap
        .timeline({ delay: 0.15 })
        .fromTo(el.querySelector("[data-dark-panel]"), { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 1.1, ease: "silk" })
        .add(fadeUp(q("[data-blog-eyebrow]")), 0.25)
        .add(blurReveal(q("#blog-title [data-tr-unit]"), { stagger: 0.05 }), 0.35)
        .add(fadeUp(q("[data-blog-sub]")), 0.8)
        .fromTo(q("[data-blog-featured]"), { opacity: 0, y: 70, scale: 0.97 }, { opacity: 1, y: 0, scale: 1, duration: 1.4, ease: "silk" }, 0.75);
    },
    { scope: root },
  );

  return (
    <div ref={root} className="pt-[calc(var(--nav-h)+10px)]">
      <DarkPanel
        enter={false}
        ariaLabelledby="blog-title"
        threads={[10, 24, 76, 90]}
        panelClassName="pb-[clamp(20px,3vw,40px)] pt-[clamp(56px,7vw,100px)]"
      >
        <div className="mx-auto flex w-[min(1180px,calc(100%-2*var(--pad)))] flex-col items-center text-center">
          <div data-blog-eyebrow="" data-reveal-init="">
            <Eyebrow tone="dark">{BLOG.eyebrow}</Eyebrow>
          </div>
          <TextReveal
            as="h1"
            id="blog-title"
            mode="manual"
            text={BLOG.title}
            gradient={GRADIENT}
            className="mt-5 max-w-[15em] text-[clamp(2.1rem,4vw,3.8rem)] font-[450] leading-[1.06] tracking-[-0.042em]"
          />
          <p data-blog-sub="" data-reveal-init="" className="mt-5 max-w-[36rem] text-[15px] leading-[1.6] text-white/60">
            {BLOG.text}
          </p>

          <a
            href={postUrl(post.slug)}
            data-blog-featured=""
            data-reveal-init=""
            className="group mt-[clamp(40px,5vw,64px)] grid w-full grid-cols-1 overflow-hidden rounded-[26px] border border-white/[0.08] bg-[#0b0b0c] p-2 text-left transition-[scale] duration-200 active:scale-[0.995] md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]"
          >
            <PostCover slug={post.slug} eager className="aspect-[16/10] rounded-[20px] md:aspect-auto md:min-h-[340px]" />
            <div className="flex flex-col p-5 md:p-8 lg:p-10">
              <div className="flex items-center gap-3">
                <span className="rounded-full bg-mint px-2.5 py-1 text-[10.5px] font-semibold text-ink">Featured</span>
                <span className="text-[11.5px] uppercase tracking-[0.1em] text-white/45">
                  {post.category} · {post.date}
                </span>
              </div>
              <h2 className="mt-6 text-[clamp(1.4rem,2.2vw,2rem)] font-[450] leading-[1.18] tracking-[-0.035em] text-white">{post.title}</h2>
              <p className="mt-4 text-[14px] leading-[1.65] text-white/55">{post.excerpt}</p>
              <span className="mt-auto flex items-center gap-1.5 pt-8 text-[14px] font-medium text-white">
                Read the guide
                <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </span>
            </div>
          </a>
        </div>
      </DarkPanel>
    </div>
  );
}
